// The one brand loader: the lotus drawn by a Lottie loop. Full screen it is the gold lotus on the brand's warm black
// (#1A1610, the guidelines' black); inside a page it stays the bronze lotus on the page colour. While the player and
// its JSON load (and always for reduced motion) the static mark pulses in its place.
import { useEffect, useRef, useState } from 'react';
import { holdAppReady } from '../lib/appReady';
import { loadLottie, loadLottieJson, prefersReducedMotion, type LottieAnimation } from '../lib/lottie';

/** The brand's dark ground for loading screens and the intro, and the gold (the foil's top) the mark and words take on it. */
const BRAND_DARK = '#1A1610';
const BRAND_GOLD = '#B8996F';

export function BrandLoader({ size = 64, label, fullscreen = false, className = '' }: { size?: number; label?: string; fullscreen?: boolean; className?: string }) {
  const dark = fullscreen;
  const box = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (prefersReducedMotion()) return;
    let anim: LottieAnimation | null = null;
    let cancelled = false;
    Promise.all([loadLottie(), loadLottieJson(dark ? '/brand/loader-dark.json' : '/brand/loader-light.json')]).then(([player, data]) => {
      if (cancelled || !box.current) return;
      anim = player.loadAnimation({ container: box.current, renderer: 'svg', loop: true, autoplay: true, animationData: data });
      anim.addEventListener('DOMLoaded', () => { if (!cancelled) setPlaying(true); });
    }).catch(() => { /* the static mark stays */ });
    return () => { cancelled = true; anim?.destroy(); };
  }, [dark]);

  const mark = (
    <div role="status" aria-label={label ?? 'Loading'} className={`flex flex-col items-center gap-2 ${fullscreen ? '' : className}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <div ref={box} className={`absolute inset-0 transition-opacity duration-150 ${playing ? 'opacity-100' : 'opacity-0'}`} aria-hidden />
        {!playing && (
          <img src={dark ? '/brand/mark-gold.svg' : '/brand/mark-light.svg'} alt="" aria-hidden className="sk-brand-pulse absolute"
            style={{ width: size * 0.6, height: size * 0.6, left: size * 0.2, top: size * 0.2 }} />
        )}
      </div>
      {label && <span className={`text-[12px] ${dark ? '' : 'text-ink-500'}`} style={{ color: dark ? BRAND_GOLD : undefined }}>{label}</span>}
    </div>
  );
  if (!fullscreen) return mark;
  return <div className={`fixed inset-0 z-50 grid place-items-center ${className}`} style={{ background: BRAND_DARK }}>{mark}</div>;
}

/** A whole page still loading: the brand loader in the middle of the content area. Holds the boot loader while shown. */
export function PageLoader({ label }: { label?: string }) {
  useEffect(() => holdAppReady(), []);
  return <div className="grid min-h-[40vh] place-items-center"><BrandLoader size={56} label={label} /></div>;
}

/**
 * The one-off brand intro after signing in: the gold Lottie wordmark on the brand's warm black, played once at 1.25×, then
 * a 300ms fade. A click (or any key) skips it. AppLayout decides when to show it.
 */
export function BrandIntro({ onDone }: { onDone: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [leaving, setLeaving] = useState(false);
  const done = useRef(onDone);
  useEffect(() => { done.current = onDone; });
  useEffect(() => {
    if (!leaving) return;
    const t = window.setTimeout(() => done.current(), 300);
    return () => window.clearTimeout(t);
  }, [leaving]);
  useEffect(() => {
    let anim: LottieAnimation | null = null;
    let cancelled = false;
    const finish = () => setLeaving(true);
    // If the player never loads, do not keep the app behind the overlay.
    const safety = window.setTimeout(finish, 6000);
    window.addEventListener('keydown', finish);
    Promise.all([loadLottie(), loadLottieJson('/brand/intro-dark.json')]).then(([player, data]) => {
      if (cancelled || !box.current) return;
      anim = player.loadAnimation({ container: box.current, renderer: 'svg', loop: false, autoplay: true, animationData: data });
      anim.setSpeed(1.25);
      anim.addEventListener('complete', finish);
    }).catch(finish);
    return () => { cancelled = true; window.clearTimeout(safety); window.removeEventListener('keydown', finish); anim?.destroy(); };
  }, []);
  return (
    <div onClick={() => setLeaving(true)} role="presentation" aria-hidden
      className={`fixed inset-0 z-[60] grid cursor-pointer place-items-center bg-[#1A1610] transition-opacity duration-300 ${leaving ? 'pointer-events-none opacity-0' : 'opacity-100'}`}>
      <div ref={box} className="aspect-[2000/560] w-[min(900px,92vw)]" />
    </div>
  );
}
